import "./SandpackHtmlPreviewComponent.scss";
import React, { useCallback, useEffect, useState } from "react";
import { SandpackProvider, SandpackPreview, SandpackFiles } from "@codesandbox/sandpack-react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { IRootReducerState } from "@/store/reducers";
import AppPreviewAndCodeStatusComponent from "@/components/common-components/app-preview-and-code-status/AppPreviewAndCodeStatusComponent";
import CommonService from "@/services/common.service";
import { useIdleDetection } from "@/hooks/use-idle-detection";

// Extend Window interface for global functions
declare global {
    interface Window {
        onImageUpload?: (file: File) => Promise<string | null>;
        onGrapesJSChange?: (htmlContent: string) => void;
        onGrapesJSSave?: (htmlContent: string, cssContent: string) => void;
        reloadSandpackPreview?: () => void;
    }
}
// Utility imports
import { extractCssAndJsContent } from "./utils/content-extractors";
import { convertReduxCodeToSandpackFiles, convertFileContentsToSandpackFiles } from "./utils/file-converters";
// Hook imports
import { useStreamingManager } from "./hooks/useStreamingManager";
import { useGrapesJSIntegration } from "./hooks/useGrapesJSIntegration";
import { useGrapesJSManualSave } from "./hooks/useGrapesJSManualSave";
import ErrorDisplayCard from "../error-display-card/ErrorDisplayCardComponent";

interface SandpackHtmlPreviewComponentProps {
    width?: string;
    height?: string;
}

// Main component
const SandpackHtmlPreviewComponent = (props: SandpackHtmlPreviewComponentProps) => {
    const { width = "100%", height = "100%" } = props;
    const params = useParams();
    const projectId = params.projectId;
    

    // Redux state
    const tempCode = useSelector((state: IRootReducerState) => state.projectData.tempCode);
    const isEditMode = useSelector((state: IRootReducerState) => state.projectData.isEditMode);
    const selectedHtmlFileFromStore = useSelector((state: IRootReducerState) => state.projectData.selectedHtmlFile);

    // Local state
    const [sandpackFiles, setSandpackFiles] = useState<SandpackFiles>({});
    const [filesVersion, setFilesVersion] = useState<number>(0);
    const [availableHtmlFiles, setAvailableHtmlFiles] = useState<string[]>([]);
    const [selectedHtmlFile, setSelectedHtmlFile] = useState<string>('index.html');
    const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
    const [reloadKey, setReloadKey] = useState<number>(0);

    // Use selected HTML file from Redux store
    useEffect(() => {
        if (selectedHtmlFileFromStore) {
            setSelectedHtmlFile(selectedHtmlFileFromStore);
        }
    }, [selectedHtmlFileFromStore]);

    // Custom hooks
    const { isStreaming, htmlInProgress, fileContents } = useStreamingManager(projectId);
    const { setupGrapesJSCallbacks, updateFileSelector } = useGrapesJSIntegration(selectedHtmlFile, isEditMode);
    const { handleGrapesJSSave } = useGrapesJSManualSave(selectedHtmlFile);

    // Handle file selection change from GrapesJS
    const handleFileChange = useCallback((fileName: string) => {
        setSelectedHtmlFile(fileName);
        
        // Reload editor with the new file
        const codeToUse = tempCode ;
        if (codeToUse && codeToUse !== '{}') {
            // Extract CSS and JS content synchronously
            const { cssContent, jsContent } = extractCssAndJsContent(codeToUse);
            
            const newFiles = convertReduxCodeToSandpackFiles(codeToUse, fileName, cssContent, jsContent, isEditMode);
            if (Object.keys(newFiles).length > 0) {
                setSandpackFiles(newFiles);
                setFilesVersion((prev: number) => prev + 1);
            }
        }
    }, [tempCode, isEditMode]);

    // Handle idle timeout - reload Sandpack when browser is idle for 2 minutes
    const handleIdleTimeout = useCallback(() => {
        // Trigger reload via RxJS subject to avoid circular dependency
        CommonService._communications.reloadPreviewSubject.next("reload");
    }, []);

    // Use idle detection hook - 2 minutes = 120000 milliseconds
    const { resetTimer } = useIdleDetection({
        idleTime: 50000, // 2 minutes
        onIdle: handleIdleTimeout,
        enabled: Object.keys(sandpackFiles).length > 0 // Only enable when Sandpack is active
    });

    // Function to reload Sandpack by changing the key
    const reloadSandpack = useCallback(() => {
        setReloadKey(prev => prev + 1);
        setFilesVersion(prev => prev + 1);
        // Reset idle timer after manual reload
        resetTimer();
    }, [resetTimer]);

    // Listen for reload requests
    useEffect(() => {
        const sub = CommonService._communications.reloadPreviewSubject.subscribe((data: string | null) => {
            if (data === "reload") {
                reloadSandpack();
            }
        });
        return () => {
            sub.unsubscribe();
        };
    }, [reloadSandpack]);

    // Update Sandpack files from streaming content
    const updateSandpackFiles = useCallback(() => {
        const now = Date.now();
        const timeSinceLastUpdate = now - lastUpdateTime;
        
        // Debounce updates to prevent too frequent re-renders
        if (timeSinceLastUpdate < 1500) {
            return;
        }
        
        const newFiles = convertFileContentsToSandpackFiles(fileContents, isEditMode);

        if (Object.keys(newFiles).length > 0) {
            setSandpackFiles((prevFiles: SandpackFiles) => {
                // Only update if files have actually changed
                const hasChanges = Object.keys(newFiles).some(fileName => {
                    const newFile = newFiles[fileName];
                    const prevFile = prevFiles[fileName];
                    
                    if (!prevFile) return true; // New file
                    
                    // Compare file content
                    const newContent = typeof newFile === 'string' ? newFile : newFile?.code || '';
                    const prevContent = typeof prevFile === 'string' ? prevFile : prevFile?.code || '';
                    
                    return newContent !== prevContent;
                });
                
                if (hasChanges) {
                    const updatedFiles = { ...prevFiles, ...newFiles };
                    setFilesVersion((prev: number) => prev + 1);
                    setLastUpdateTime(now);
                    // Reset idle timer when files are updated
                    resetTimer();
                    return updatedFiles;
                }
                
                return prevFiles; // No changes, don't update
            });
        }
    }, [fileContents, isEditMode, lastUpdateTime, resetTimer]);

    // Initialize Sandpack files with code from Redux
    useEffect(() => {
        const codeToUse = tempCode ;

        if (codeToUse && codeToUse !== '{}') {
            // Extract CSS and JS content synchronously
            const { cssContent, jsContent, htmlFiles } = extractCssAndJsContent(codeToUse);
            
            // Update available HTML files
            setAvailableHtmlFiles(htmlFiles);

            // Set default selected file if not already set or if current selection doesn't exist
            if (!htmlFiles.includes(selectedHtmlFile)) {
                const defaultFile = htmlFiles.find(f => f.includes('index')) || htmlFiles[0] || 'index.html';
                setSelectedHtmlFile(defaultFile);
            }
            
            const initialFiles = convertReduxCodeToSandpackFiles(codeToUse, selectedHtmlFile, cssContent, jsContent, isEditMode);

            if (Object.keys(initialFiles).length > 0) {                
                setSandpackFiles((prevFiles: SandpackFiles) => {
                    // Only update if there are actual changes
                    const hasChanges = Object.keys(initialFiles).some(fileName => {
                        const newFile = initialFiles[fileName];
                        const prevFile = prevFiles[fileName];
                        
                        if (!prevFile) return true; // New file
                        
                        // Compare file content
                        const newContent = typeof newFile === 'string' ? newFile : newFile?.code || '';
                        const prevContent = typeof prevFile === 'string' ? prevFile : prevFile?.code || '';
                        
                        return newContent !== prevContent;
                    });
                    
                    if (hasChanges) {
                        const updatedFiles = { ...prevFiles, ...initialFiles };
                        setFilesVersion((prev: number) => prev + 1);
                        setLastUpdateTime(Date.now());
                        // Reset idle timer when initial files are set
                        resetTimer();
                        return updatedFiles;
                    }
                    
                    return prevFiles; // No changes, don't update
                });
            }
        }
    }, [tempCode, selectedHtmlFile, isEditMode, resetTimer]);

    // Update preview periodically during streaming
    useEffect(() => {
        if (!isStreaming) return;

        // Use a longer interval to reduce frequent updates
        const updateInterval = htmlInProgress ? 2000 : 3000;
        const intervalId = setInterval(updateSandpackFiles, updateInterval);

        return () => clearInterval(intervalId);
    }, [isStreaming, htmlInProgress, updateSandpackFiles]);

    // Final update when streaming ends
    useEffect(() => {
        if (!isStreaming && fileContents.size > 0) {
            setTimeout(updateSandpackFiles, 100);
        }
    }, [isStreaming, fileContents.size, updateSandpackFiles]);

    // Set up GrapesJS integration
    useEffect(() => {
        const cleanup = setupGrapesJSCallbacks(handleFileChange, handleGrapesJSSave);
        return cleanup;
    }, [setupGrapesJSCallbacks, handleFileChange, handleGrapesJSSave]);

    // Set up message handlers for GrapesJS communication
    useEffect(() => {
        const pendingUploads = new Map<string, { resolve: (url: string) => void; reject: (error: Error) => void }>();
        const pendingAssetSelections = new Map<string, { resolve: (asset: any) => void; reject: (error: Error) => void }>();

        // Handle messages from GrapesJS iframe
        const handleMessage = async (event: MessageEvent) => {
            const { type, messageId, file } = event.data;

            if (type === 'IMAGE_UPLOAD_REQUEST') {
                
                // Store the promise resolvers
                new Promise<string>((resolve, reject) => {
                    pendingUploads.set(messageId, { resolve, reject });
                });

                // Convert base64 back to file
                const base64Response = await new Promise<string>((resolve) => {
                    const handleDataMessage = (dataEvent: MessageEvent) => {
                        if (dataEvent.data.type === 'IMAGE_UPLOAD_DATA' && dataEvent.data.messageId === messageId) {
                            window.removeEventListener('message', handleDataMessage);
                            resolve(dataEvent.data.data);
                        }
                    };
                    window.addEventListener('message', handleDataMessage);
                });

                // Convert base64 to file
                const base64Data = base64Response.split(',')[1];
                const binaryData = atob(base64Data);
                const bytes = new Uint8Array(binaryData.length);
                for (let i = 0; i < binaryData.length; i++) {
                    bytes[i] = binaryData.charCodeAt(i);
                }
                const fileBlob = new Blob([bytes], { type: file.type });
                const fileObject = new File([fileBlob], file.name, { type: file.type });

                try {
                    // Create payload for API
                    const payload = {
                        file_name: fileObject.name,
                        content_type: fileObject.type,
                    };

                    // Call the project service to get upload URL
                    const response = await CommonService._project_service.createProjectAssetUrl(projectId!, payload);
                    const urlDetails = response?.data;

                    if (!urlDetails?.upload_url) {
                        throw new Error('No upload URL received');
                    }

                    // Upload the file to the provided URL
                    const uploadResponse = await fetch(urlDetails.upload_url, {
                        method: 'PUT',
                        body: fileObject,
                        headers: {
                            'Content-Type': fileObject.type,
                            ...urlDetails.headers,
                        },
                    });

                    if (!uploadResponse.ok) {
                        throw new Error('Upload failed');
                    }

                    
                    // Send success response back to GrapesJS
                    const resolvers = pendingUploads.get(messageId);
                    if (resolvers) {
                        resolvers.resolve(urlDetails.public_url);
                        pendingUploads.delete(messageId);
                    }

                    // Send response to iframe
                    const iframe = document.querySelector('iframe');
                    if (iframe && iframe.contentWindow) {
                        iframe.contentWindow.postMessage({
                            type: 'IMAGE_UPLOAD_RESPONSE',
                            messageId: messageId,
                            success: true,
                            imageUrl: urlDetails.public_url
                        }, '*');
                    }

                } catch (error) {
                    console.error('Image upload error:', error);
                    CommonService._alert.showToast('Image upload failed', 'error');
                    
                    // Send error response back to GrapesJS
                    const resolvers = pendingUploads.get(messageId);
                    if (resolvers) {
                        resolvers.reject(error instanceof Error ? error : new Error(String(error)));
                        pendingUploads.delete(messageId);
                    }

                    // Send error response to iframe
                    const iframe = document.querySelector('iframe');
                    if (iframe && iframe.contentWindow) {
                        iframe.contentWindow.postMessage({
                            type: 'IMAGE_UPLOAD_RESPONSE',
                            messageId: messageId,
                            success: false,
                            error: error instanceof Error ? error.message : String(error)
                        }, '*');
                    }
                }
            }

            if (type === 'ASSET_SELECTION_REQUEST') {
                
                // Store the promise resolvers
                new Promise<any>((resolve, reject) => {
                    pendingAssetSelections.set(messageId, { resolve, reject });
                });

                try {
                    // Trigger asset selector
                    CommonService._communications.toggleFileUploadSubject.next(true);
                    
                    // Set up a one-time listener for asset selection
                    const assetSelectionSub = CommonService._communications.toggleFileUploadSubject.subscribe((isOpen) => {
                        if (!isOpen) {
                            // Dialog closed, send cancelled response
                            assetSelectionSub.unsubscribe();
                            
                            const resolvers = pendingAssetSelections.get(messageId);
                            if (resolvers) {
                                resolvers.reject(new Error('Selection cancelled'));
                                pendingAssetSelections.delete(messageId);
                            }

                            // Send response to iframe
                            const iframe = document.querySelector('iframe');
                            if (iframe && iframe.contentWindow) {
                                iframe.contentWindow.postMessage({
                                    type: 'ASSET_SELECTION_RESPONSE',
                                    messageId: messageId,
                                    success: false,
                                    error: 'Selection cancelled'
                                }, '*');
                            }
                        }
                    });
                    
                    // Timeout after 30 seconds
                    setTimeout(() => {
                        const resolvers = pendingAssetSelections.get(messageId);
                        if (resolvers) {
                            resolvers.reject(new Error('Selection timeout'));
                            pendingAssetSelections.delete(messageId);
                        }
                    }, 30000);

                } catch (error) {
                    console.error('Asset selection error:', error);
                    
                    // Send error response back to GrapesJS
                    const resolvers = pendingAssetSelections.get(messageId);
                    if (resolvers) {
                        resolvers.reject(error instanceof Error ? error : new Error(String(error)));
                        pendingAssetSelections.delete(messageId);
                    }

                    // Send error response to iframe
                    const iframe = document.querySelector('iframe');
                    if (iframe && iframe.contentWindow) {
                        iframe.contentWindow.postMessage({
                            type: 'ASSET_SELECTION_RESPONSE',
                            messageId: messageId,
                            success: false,
                            error: error instanceof Error ? error.message : String(error)
                        }, '*');
                    }
                }
            }
        };

        window.addEventListener('message', handleMessage);

        // Cleanup function
        return () => {
            window.removeEventListener('message', handleMessage);
            pendingUploads.clear();
            pendingAssetSelections.clear();
        };
    }, [projectId]);

    // Update file selector when available HTML files change
    useEffect(() => {
        updateFileSelector(availableHtmlFiles);
    }, [availableHtmlFiles, updateFileSelector]);

    // Expose reload function globally
    useEffect(() => {
        window.reloadSandpackPreview = reloadSandpack;
        
        return () => {
            delete window.reloadSandpackPreview;
        };
    }, [reloadSandpack]);


    return (
        <div className="sandpack-html-preview-content" style={{ width, height }}>
            {isStreaming && (
                <div className="live-preview-badge">
                    App build in progress...
                </div>
            )}
            
            {Object.keys(sandpackFiles).length > 0 ? (
                <SandpackProvider
                    key={`sandpack-${filesVersion}-${reloadKey}-${isEditMode}`}
                    template="static"
                    files={sandpackFiles}
                    options={{
                        visibleFiles: ["/index.html"],
                        activeFile: "/index.html",
                        autorun: true,
                        autoReload: true,
                    }}
                    customSetup={{
                        entry: "/index.html"
                    }}
                    theme="dark"
                >
                    <SandpackPreview
                        style={{
                            height: "100%",
                            width: "100%"
                        }}
                        showOpenInCodeSandbox={false}
                        showRefreshButton={true}
                        showNavigator={false}
                    />
                </SandpackProvider>
            ) : (
                <AppPreviewAndCodeStatusComponent
                    type="info"
                    title="Apps Built by AI. Ideas Powered by You."
                    description="Build full web apps just by describing them. Our AI handles the code so you can focus on what matters—your ideas."
                />
            )}
            <ErrorDisplayCard isLoading={isStreaming}/>

        </div>
    );
};

export default SandpackHtmlPreviewComponent; 
